import React, { memo, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import TextField from 'modules/views/shared/TextField';
import MobileInput from 'modules/views/shared/MobileInput';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import Modal from 'modules/views/shared/Modal';
import NavigationButtons from 'modules/views/shared/NavigationButtons';

import { useWizardStepNavigation } from 'modules/views/shared/Wizard/hooks';
import useForm from 'hooks/useForm';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { setOnboarding } from 'modules/state-mgmt/onboarding';

import { actionMapForOtherLoaders } from 'constants/actions';

import { useStyles } from './styles';
import registrationSchema from './schema';
import { useCreateWorker, useFieldConstraints } from './hooks';
import { WorkerProfileField } from 'types';
import { extractNationalNumber, getBackendErrorsFromResponse, isValidMobileNumber } from 'utils';
import { ROUTES } from 'constants/routes';
import { WorkerInvitationStatus } from 'models/general';

const Registration = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { handlePreviousStep, canGoToPreviousStep, goToNextStep } = useWizardStepNavigation();
  const [showUnverifiedWorkerWarning, setShowUnverifiedWorkerWarning] = useState(false);

  const {
    isCreatingWorker,
    onboardingTemp,
    profileFieldConstraintsMap,
    canCreateUnverifiedWorkers,
    hasUnverifiedWorkerAccess,
    fetchAllCertificationsLoading,
    fetchCertificationGroupsLoading,
    isOffline,
    isWorker,
    uploadingDetails,
    workerData,
  } = useAppSelector(state => ({
    isCreatingWorker: state.loader.otherLoaders?.[actionMapForOtherLoaders.createWorker]?.isLoading || false,
    fetchCertificationGroupsLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.fetchCertificationGroups],
    fetchAllCertificationsLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.fetchAllCertifications],
    onboardingTemp: state.onboarding?.temp || {},
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    canCreateUnverifiedWorkers: state.cache.canCreateUnverifiedWorkers,
    hasUnverifiedWorkerAccess: state.user.userId && !state.user.isWorker,
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    isWorker: !state.user?.userId || state.user?.isWorker,
    uploadingDetails: state.onboarding?.uploadingDetails,
    workerData: {
      firstName: state.onboarding?.firstName,
      lastName: state.onboarding?.lastName,
      email: state.onboarding?.email,
      mobilePhoneNumber: state.onboarding?.mobilePhoneNumber,
      dateOfBirth: state.onboarding?.dateOfBirth,
    },
  }));

  const isLoading =
    isCreatingWorker || fetchAllCertificationsLoading?.isLoading || fetchCertificationGroupsLoading?.isLoading;

  const { handleCreateWorker } = useCreateWorker({
    onFailure: backendErrors => {
      setErrors(backendErrors);
    },
  });

  const onSubmit = (values: any) => {
    // Clear upload errors from state when user submits
    const hasValidPhone = extractNationalNumber(values.mobilePhoneNumber);

    if (hasValidPhone || values.email?.trim() || hasUnverifiedWorkerAccess) {
      if (isOffline && !isWorker) {
        const isUnverifiedWorker = !values.email?.trim() && !isValidMobileNumber(values.mobilePhoneNumber);
        dispatch(
          setOnboarding({
            firstName: onboardingTemp?.firstName || workerData?.firstName || '',
            lastName: onboardingTemp?.lastName || workerData?.lastName || '',
            dateOfBirth: onboardingTemp?.dateOfBirth || workerData?.dateOfBirth || null,
            email: values.email,
            mobilePhoneNumber: isValidMobileNumber(values.mobilePhoneNumber) ? values.mobilePhoneNumber : null,
            ...(isUnverifiedWorker ? { invitationStatus: WorkerInvitationStatus.NOT_INVITED } : {}),
            temp: {
              ...(onboardingTemp || {}),
              project: onboardingTemp?.project || null,
              employers: onboardingTemp?.employers || null,
              addEmployerFlow: onboardingTemp?.addEmployerFlow || false,
            },
            uploadingDetails: {
              ...(uploadingDetails || {}),
              isOffline: true,
              pendingUploads: {
                ...(uploadingDetails?.pendingUploads || {}),
                workerCreation: true,
                ...(isUnverifiedWorker ? { employers: true } : {}),
              },
              errors:
                uploadingDetails?.errors?.pageUrl === ROUTES.REGISTRATION.path
                  ? { pageUrl: '', errorObject: null }
                  : uploadingDetails?.errors,
            },
          }),
        );
        goToNextStep();
        return;
      }
      handleCreateWorker(values);
    } else {
      setShowUnverifiedWorkerWarning(true);
    }
  };

  const {
    isEmailMandatory,
    isPhoneMandatory,
    isEmailVisible,
    isPhoneVisible,
    isEitherEmailOrPhoneVisible,
    isOrTextVisible,
  } = useFieldConstraints(profileFieldConstraintsMap, canCreateUnverifiedWorkers);

  const { email, mobilePhoneNumber } = onboardingTemp || {};

  const { values, errors, handleSubmit, setValue, setErrors } = useForm({
    initialValues: {
      email: email || workerData?.email || '',
      mobilePhoneNumber: mobilePhoneNumber || workerData?.mobilePhoneNumber || '',
    },
    validationSchema: registrationSchema(t, {
      canCreateUnverifiedWorkers,
      emailConstraints: profileFieldConstraintsMap[WorkerProfileField.email],
      mobilePhoneNumberConstraints: profileFieldConstraintsMap[WorkerProfileField.mobilePhoneNumber],
      emailOrMobilePhoneNumberConstraints: profileFieldConstraintsMap[WorkerProfileField.emailOrMobilePhoneNumber],
    }),
    onSubmit: onSubmit,
  });

  const submitOnEnter = useSubmitOnEnter(handleSubmit, isLoading);

  // On mount, populate form errors from background upload errors (if any)
  useEffect(() => {
    if (uploadingDetails?.errors?.pageUrl === ROUTES.REGISTRATION.path && uploadingDetails?.errors?.errorObject) {
      setErrors(getBackendErrorsFromResponse(uploadingDetails.errors.errorObject, ['email', 'mobilePhoneNumber']));
    }
  }, []);

  const fields = useMemo(() => {
    return [
      {
        name: t('workerRegistration.email'),
        id: 'email',
        isRequired: isEmailMandatory,
      },
      {
        name: t('workerRegistration.phoneNumber'),
        id: 'mobilePhoneNumber',
        isRequired: isPhoneMandatory,
      },
    ];
  }, [t, isEmailMandatory, isPhoneMandatory]);

  const content = (
    <div>
      <ContentHeader title={t('workerRegistration.welcome')} />
      <Grid container spacing={3} style={{ marginTop: 28 }}>
        {(isEmailVisible || isEitherEmailOrPhoneVisible) && (
          <Grid item xs={12}>
            <TextField
              label={t('workerRegistration.email')}
              name="email"
              required={isEmailMandatory}
              value={values.email}
              error={errors.email?.message}
              testId="email-input"
              onChange={e => setValue('email', e.target.value?.trim())}
              onKeyDown={submitOnEnter}
            />
          </Grid>
        )}

        {isOrTextVisible && (
          <p className={classes.orTextParent}>
            <span className={classes.orText}>{t('common.orSmall')}</span>
          </p>
        )}

        {(isPhoneVisible || isEitherEmailOrPhoneVisible) && (
          <Grid item xs={12}>
            <MobileInput
              label={t('workerRegistration.phoneNumber')}
              required={isPhoneMandatory}
              placeholder={t('workerRegistration.phoneNumber')}
              value={values.mobilePhoneNumber}
              error={errors.mobilePhoneNumber?.message}
              testId="mobilePhoneNumber-input"
              onChange={({ phone }) => setValue('mobilePhoneNumber', phone)}
              onKeyDown={submitOnEnter}
            />
          </Grid>
        )}
      </Grid>

      <NavigationButtons
        secondaryButtonProps={{
          isHidden: !canGoToPreviousStep,
          onClick: handlePreviousStep,
          testId: 'registration-back-button',
        }}
        primaryButtonProps={{
          isLoading: isLoading,
          hasEndArrow: true,
          testId: 'registration-next-button',
          onClick: handleSubmit,
        }}
      />
      {showUnverifiedWorkerWarning && (
        <Modal
          title={t('common.warning')}
          Content={<p>{t('workerRegistration.unverifiedWorkerWarning')}</p>}
          actionButtons={[
            {
              label: t('common.close'),
              onClick: () => {
                setShowUnverifiedWorkerWarning(false);
              },
              testId: 'registration-unverified-warning-close-button',
            },
          ]}
          onClose={() => setShowUnverifiedWorkerWarning(false)}
        />
      )}
    </div>
  );

  return <WithWizard disableNavigation={isLoading} fields={fields} content={content} />;
};

export default memo(Registration);
